import React, { useState, useRef } from 'react';
import './MediaUpload.scss';
import { closeIcon } from '../../utils/images';
import { addMedia } from '../../services/mediaList';
import { MediaUploadRequest } from '../../types/Media';


export const MediaUpload: React.FC<{ 
  onUpload: () => void;
  onSuccess?: () => void;
}> = ({ onUpload, onSuccess }) => {
  const [mediaName, setMediaName] = useState<string>('');
  const [baseImage, setBaseImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [bucketName] = useState<string>('display-images');
  const [fpath] = useState<string>('base-images');
  

  // Handle file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file); // Store the actual file
      const reader = new FileReader();
      reader.onload = () => {
        setBaseImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Trigger file input click
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  // Handle media upload
  const handleConfirmUpload = async () => {
    if (!uploadedFile) {
      alert('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      
      const mediaData: MediaUploadRequest = {
        files: [uploadedFile],
        bucket_name: bucketName,
        fpath: fpath
      };

      const response = await addMedia(mediaData);
      
      if (response.message === 'Files uploaded successfully') {
        alert(`Media uploaded successfully: ${response.uploaded_files[0].file_name}`);
        // Reset form
        setMediaName('');
        setBaseImage(null);
        setUploadedFile(null);
        // Call success callback to refresh the media library
        if (onSuccess) {
          onSuccess();
        }
        // Close the modal
        onUpload();
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="media-upload-overlay">
    <div className="media-upload">
      <div className="media-upload__header">
        <div className="media-upload__close">
          <img className="media-upload__close--icon" src={closeIcon} alt="close" onClick={onUpload}/>
        </div>
        <h1 className="media-upload__title">Add Media</h1>
        <div className="media-upload__actions">
          <button 
            className="media-upload__cancel-button"
            onClick={onUpload}
          >
            Cancel
          </button>
          <button 
            className="media-upload__confirm-button"
            onClick={handleConfirmUpload}
            disabled={uploading || !uploadedFile}
          >
            {uploading ? 'Uploading...' : 'Confirm'}
          </button>
        </div>
      </div>

      <div className="media-upload__form-grid">
        <div className="media-upload__form-field">
          <label className="media-upload__label media-upload__label--first">Media Name</label>
          <input
            type="text"
            className="media-upload__input"
            value={mediaName}
            onChange={(e) => setMediaName(e.target.value)}
            placeholder="Type a name"
          />
        </div>
      </div>

      <div className="media-upload__upload-area">
        <input 
          type="file" 
          ref={fileInputRef}
          className="media-upload__file-input" 
          accept="image/*" 
          onChange={handleFileUpload} 
          style={{ display: 'none' }}
        />
        {baseImage ? (
          <img 
            src={baseImage} 
            alt="Base product" 
            className="media-upload__image media-upload__preview-image" 
          />
        ) : (
          <div className="media-upload__image media-upload__upload-placeholder">
            <input 
              type="text"
              className="media-upload__image--input"
              placeholder="Upload"
              value={baseImage || ""}
              onChange={(e) => setBaseImage(e.target.value)}
            />
          </div>
        )}
        <button 
          className="media-upload__upload-button"
          onClick={triggerFileInput}
          type="button"
        >
          {baseImage ? 'Change Image' : 'Upload Image'}
        </button>
      </div>
    </div>
    </div>
  );
};